import { showErrorNotification } from "molecules/notifications/notificationEvents";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLazyGetFortnoxAuthorizationUrlQuery } from "src/common/service/fusionHub/integrations/fortnoxApi";
import { URL_SYSTEM_IDENTIFIER } from "src/features/integrations/sources/fortnox/constants";

export const useAuthorizationUrl = (companyDomainId: string) => {
	const [createLink, { isLoading: isCreatingUrl }] =
		useLazyGetFortnoxAuthorizationUrlQuery();

	const isProcessingRef = useRef(false);
	const { t } = useTranslation();

	return {
		isLoading: isCreatingUrl,
		onRequestAuthorizationUrl: useCallback(
			async (external?: boolean, scopes?: string[]) => {
				if (isProcessingRef.current) {
					return;
				}

				let url: URL;
				if (external) {
					url = new URL("/fortnox-external-callback", location.origin);
				} else {
					url = new URL(location.pathname, location.origin);
					url.searchParams.set("system", URL_SYSTEM_IDENTIFIER);
					if (scopes?.length) {
						for (const scope of scopes) {
							url.searchParams.append("scopes", scope);
						}
					}
				}

				const result = await createLink({
					companyDomainId,
					successRedirectUrl: url.toString(),
					errorRedirectUrl: url.toString(),
					...(scopes?.length && { additionalScopes: scopes }),
				});

				isProcessingRef.current = false;

				if ("error" in result) {
					showErrorNotification({
						message: t("Failed to initialize integration with Fortnox."),
					});
					return null;
				}
				return result.data!.authorizationUrl;
			},
			[createLink, companyDomainId, t],
		),
	};
};
